import clsx from 'clsx'
import escapeRegExp from 'lodash/escapeRegExp'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, {
  FC,
  Fragment,
  memo,
  ReactNode,
  useContext,
} from 'react'

import AngleRight from '@Assets/SVG/AngleRight'
import ArrowRightThinIcon from '@Assets/SVG/ArrowRightThin'
import SearchIcon from '@Assets/SVG/Search'
import { Picture } from '@Components/Cloudinary/Picture'
import { SVGIcon } from '@Components/Icons'
import { Link } from '@Components/Link'
import { Wrapper } from '@Components/Search/QueryResults/styled.index'
import { NavigationEvent } from '@Events/Navigation'
import { MetadataContext } from '@Globals/Metadata'
import { formatPrice } from '@Globals/PriceHelpers'
import {
  AlgoliaBrand,
  AlgoliaCategory,
  AlgoliaProduct,
  SearchResults,
} from '@Globals/Search/Algolia'
import { addPreviousSearch, PreviousSearchItemTypes } from '@Globals/Search/PreviousSearches'
import { SearchSelection } from '@Globals/Search/Selection'
import { useTranslation } from '@Globals/Translations'

interface HighlightTitleProps {
  query: string
  title: string
}

interface QueryResultsProps {
  query: string
  results: SearchResults
  selection: SearchSelection
}

interface PriceProps {
  beforePrice: number
  price: number
}

interface SearchItemProps {
  query: string
  selected: boolean
}

type BrandItemProps = AlgoliaBrand & SearchItemProps
type CategoryItemProps = AlgoliaCategory & SearchItemProps
type CategoryItemBodyProps = Omit<CategoryItemProps, 'selected' | 'slug' | 'objectID'>
type ProductItemProps = AlgoliaProduct & SearchItemProps

const HighlightTitle: FC<HighlightTitleProps> = memo(({ query, title }: HighlightTitleProps) => {
  const re = new RegExp(escapeRegExp(query), 'gi')
  let match = re.exec(title)
  let i = 0
  const titleParts: ReactNode[] = []
  while (match !== null) {
    const end = match.index + match[0].length
    if (i !== match.index) {
      titleParts.push(<Fragment key={i}>{title.substring(i, match.index)}</Fragment>)
    }
    titleParts.push(<em key={match.index}>{title.substring(match.index, end)}</em>)
    i = end
    match = re.exec(title)
  }
  titleParts.push(<Fragment key={i}>{title.substring(i, title.length)}</Fragment>)

  // in case no matches found
  if (titleParts.length === 0) {
    titleParts.push(title)
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{titleParts}</>
})

const BrandItem: FC<BrandItemProps> = ({
  objectID,
  query,
  selected,
  slug,
  title,
}: BrandItemProps) => {
  const { storeURL } = useContext(MetadataContext)
  return (
    <Link
      className={clsx({
        searchResultsBrandItem: true,
        selectedItem: selected,
      })}
      event={new NavigationEvent({
        attributes: {
          dest_page_type: 'brand',
          dest_page_type_id: objectID,
          search_query: query,
          source_id: 'site_search_results',
        },
        eventName: 'nav_link_click',
      })}
      href={storeURL(slug)}
      onClick={() => addPreviousSearch({
        id: objectID,
        slug,
        title,
        type: PreviousSearchItemTypes.Brand,
      })}
    >
      <div className="arrowIcon">
        <SVGIcon icon={ArrowRightThinIcon} size="16px" />
      </div>
      <span><HighlightTitle query={query} title={title} /></span>
    </Link>
  )
}

const CategoryItemBody: FC<CategoryItemBodyProps> = ({
  depth,
  parentTitle,
  query,
  title,
}: CategoryItemBodyProps) => {
  if (depth === 1) {
    return (
      <div className="textWrapper">
        <div className="innerText">
          <span><HighlightTitle query={query} title={title} /></span>
        </div>
      </div>
    )
  }
  return (
    <div className="textWrapper">
      <div className="innerText">
        <span>{parentTitle}</span>
        <div className="divideIcon">
          <SVGIcon icon={AngleRight} size="8px" />
        </div>
        <span><HighlightTitle query={query} title={title} /></span>
      </div>
    </div>
  )
}

const CategoryItem: FC<CategoryItemProps> = ({
  objectID,
  parentTitle,
  query,
  selected,
  slug,
  title,
  ...props
}: CategoryItemProps) => {
  const { storeURL } = useContext(MetadataContext)
  return (
    <Link
      className={clsx({
        searchResultsCategoriesItem: true,
        selectedItem: selected,
      })}
      event={new NavigationEvent({
        attributes: {
          dest_page_type: 'category',
          dest_page_type_id: objectID,
          search_query: query,
          source_id: 'site_search_results',
        },
        eventName: 'nav_link_click',
      })}
      href={storeURL(slug)}
      onClick={() => addPreviousSearch({
        id: objectID,
        parentTitle,
        slug,
        title,
        type: PreviousSearchItemTypes.Category,
      })}
    >
      <div className="arrowRightThinIconWrapper">
        <SVGIcon icon={ArrowRightThinIcon} size="16px" />
      </div>
      <CategoryItemBody
        parentTitle={parentTitle}
        query={query}
        title={title}
        {...props}
      />
    </Link>
  )
}

const Price: FC<PriceProps> = ({ beforePrice, price }: PriceProps) => {
  const { store } = useContext(MetadataContext)
  const { locale = '' } = useRouter()

  if (beforePrice) {
    return (
      <>
        <div className="beforePrice">
          {formatPrice(store?.id, locale, beforePrice)}
        </div>
        <div className="discountPrice">
          {formatPrice(store?.id, locale, price)}
        </div>
      </>
    )
  }

  return (
    <div className="normalPrice">
      {formatPrice(store?.id, locale, price)}
    </div>
  )
}

const ProductItem: FC<ProductItemProps> = ({
  beforePrice,
  brand,
  objectID,
  price,
  primaryImage,
  query,
  selected,
  slug,
  title,
}: ProductItemProps) => {
  const { storeURL } = useContext(MetadataContext)
  return (
    <Link
      className={clsx({
        searchResultsProductItem: true,
        selectedItem: selected,
      })}
      event={new NavigationEvent({
        attributes: {
          dest_page_type: 'product',
          dest_page_type_id: objectID,
          search_query: query,
          source_id: 'site_search_results',
        },
        eventName: 'nav_link_click',
      })}
      href={storeURL(slug)}
      onClick={() => {
        addPreviousSearch({
          brand,
          id: objectID,
          slug,
          title,
          type: PreviousSearchItemTypes.Product,
        })
      }}
    >
      <div className="imgWrap">
        <Picture
          alt={`${title} - search item`}
          options={{
            height: 90,
            path: primaryImage,
            width: 90,
          }}
        />
      </div>
      <div className="infoWrap">
        <span>
          {`${brand} - `}
          <HighlightTitle query={query} title={title} />
        </span>
      </div>
      <div className="priceWrap">
        <Price beforePrice={beforePrice} price={price} />
      </div>
    </Link>
  )
}

export const QueryResults: FC<QueryResultsProps> = (
  {
    query,
    results,
    selection,
  }: QueryResultsProps,
) => {
  const { brands, categories, products } = results
  const { t } = useTranslation()
  const { storeURL } = useContext(MetadataContext)

  const searchAllURL = storeURL('catalogsearch/result', new URLSearchParams({ q: query }))

  return (
    <Wrapper onClick={e => e.stopPropagation()}>
      <div className="searchResultsCategories">
        {map(categories, c => (
          <CategoryItem
            key={c.objectID}
            query={query}
            selected={selection.type === 'category' && selection.objectID === c.objectID}
            {...c}
          />
        ))}
        <Link
          className={clsx({
            freeText: true,
            searchResultsCategoriesItem: true,
            selectedItem: selection.type === 'freetext',
          })}
          event={new NavigationEvent({
            attributes: {
              dest_page_type: 'searchresults',
              dest_page_type_id: '',
              search_query: query,
              source_id: 'site_search_results',
            },
            eventName: 'nav_link_click',
          })}
          href={searchAllURL}
        >
          <div className="searchIconWrapper">
            <SVGIcon icon={SearchIcon} size="16px" />
          </div>
          <b>{query}</b>
          <div className="searchAllArrowRightThinIconWrapper">
            <SVGIcon icon={ArrowRightThinIcon} size="12px" />
          </div>
          <span className="searchAll">{t('header:SEARCH_ALL')}</span>
        </Link>
      </div>
      <div className="brandAndCategoryWrapper">
        <div className="brands">
          <span className="title">{t('header:BRANDS')}</span>
          {isEmpty(brands)
            ? <div className="nothingToShowText">{t('header:NO_BRANDS_TO_SHOW')}</div>
            : map(brands, b => (
              <BrandItem
                key={b.objectID}
                query={query}
                selected={selection.type === 'brand' && selection.objectID === b.objectID}
                {...b}
              />
            ))}
        </div>
        <div className="products">
          <span className="title">{t('header:PRODUCTS')}</span>
          {isEmpty(products)
            ? <div className="nothingToShowText">{t('header:NO_PRODUCTS_TO_SHOW')}</div>
            : map(products, p => (
              <ProductItem
                key={p.objectID}
                query={query}
                selected={selection.type === 'product' && selection.objectID === p.objectID}
                {...p}
              />
            ))}
        </div>
      </div>
    </Wrapper>
  )
}
